import { Flex } from '@radix-ui/themes'
import { useQuery, useSuspenseInfiniteQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { conversationQueryOptions, listConversationsInfiniteQuery, messageQueryOptions } from '~/queries'
import { Spinner } from '~/elementsv2'
import { Boundary, Page } from '~/componentsV2'
import { z } from 'zod'
import { useInfiniteScroll } from '~/hooks'
import { ConversationsQueryParamBuilder, ConversationsTable } from '../../../../../../features/conversations/components'

const conversationsSearchSchema = z.object({
  conversationId: z
    .string()
    .optional()
    .catch(() => undefined),
  messageId: z
    .string()
    .optional()
    .catch(() => undefined),
  integrationName: z
    .string()
    .optional()
    .catch(() => undefined),
  participantIds: z
    .array(z.string())
    .optional()
    .catch(() => undefined),
  tags: z
    .record(z.string())
    .optional()
    .catch(() => undefined),
})

export const Route = createFileRoute('/workspaces/$workspaceId/bots/$botId/conversations/')({
  validateSearch: conversationsSearchSchema,
  component: Component,
})

function Component() {
  return (
    <Boundary height={'200px'} loaderSize="6">
      <ConversationsPage />
    </Boundary>
  )
}

const ConversationsPage = () => {
  const { workspaceId, botId } = Route.useParams()

  return (
    <Page title="Conversations">
      <Flex direction={'column'} gap={'4'}>
        <ConversationsQueryParamBuilder workspaceId={workspaceId} botId={botId} />
        <Boundary loaderSize={'6'}>
          <ConversationList />
        </Boundary>
      </Flex>
    </Page>
  )
}

const ConversationList = () => {
  const { workspaceId, botId } = Route.useParams()
  const { conversationId, messageId, integrationName, tags, participantIds } = Route.useSearch()

  const {
    data: fetchedConversations,
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = useSuspenseInfiniteQuery(
    listConversationsInfiniteQuery({ botId, workspaceId, integrationName, tags, participantIds })
  )
  const bottomRef = useInfiniteScroll(fetchNextPage, hasNextPage)

  const { data: fetchedMessage } = useQuery({
    ...messageQueryOptions({ messageId: messageId ?? '', botId, workspaceId }),
    enabled: !!messageId,
  })

  const { data: fetchedConversation } = useQuery({
    ...conversationQueryOptions({
      botId,
      workspaceId,
      conversationId: conversationId ?? fetchedMessage?.message.conversationId ?? '',
    }),
    enabled: !!conversationId || !!fetchedMessage?.message.conversationId,
  })

  const conversations = fetchedConversation?.conversation
    ? [fetchedConversation.conversation]
    : (fetchedConversations.pages?.flatMap((page) => page.conversations) ?? [])

  return (
    <>
      <ConversationsTable conversations={conversations} workspaceId={workspaceId} botId={botId} />

      <div ref={bottomRef} />
      {isFetching && (
        <Flex className="w-full items-center justify-center">
          <Spinner size="6" />
        </Flex>
      )}
    </>
  )
}
